import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import type { Holder, RiskRating } from '../../data/types';
import { nextReviewDate, suggestRisk } from '../../domain/kyc/riskRating';
import { date, DEMO_TODAY } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { sumBy, useTenantScope } from '../../store/selectors';
import { useToast } from '../../ui/feedback';
import { SectionCard, StatusChip } from '../../ui/primitives';

export default function RiskRatingCard({ holder }: { holder: Holder }) {
  const scope = useTenantScope();
  const { dispatch } = useRegistry();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const invested = sumBy(
    scope.holdings.filter((holding) => holding.holderId === holder.id),
    (holding) => holding.value,
  );
  const suggestion = suggestRisk(holder, scope.kycCases, invested);
  const [rating, setRating] = useState<RiskRating>(suggestion.rating);
  const [notes, setNotes] = useState('');
  const latest = holder.riskReviews?.[0];
  const reviewDue = latest ? latest.nextReviewDate : undefined;
  const overdue = reviewDue !== undefined && reviewDue < DEMO_TODAY;
  const differs = suggestion.rating !== holder.riskRating;

  return (
    <SectionCard
      title="Risk rating"
      subtitle="Suggested from the register, confirmed by a person"
      action={
        <Button
          size="small"
          variant={overdue || differs ? 'contained' : 'text'}
          onClick={() => {
            setRating(suggestion.rating);
            setNotes('');
            setOpen(true);
          }}
        >
          Review
        </Button>
      }
    >
      <Stack direction="row" spacing={3} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Current
          </Typography>
          <Box>
            <StatusChip status={holder.riskRating} />
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Suggested
          </Typography>
          <Box>
            <StatusChip status={suggestion.rating} />
          </Box>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Next review
          </Typography>
          <Typography variant="body2">
            {reviewDue ? date(reviewDue) : 'At next KYC refresh'}
          </Typography>
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2.5 }}>
        {suggestion.factors.map((factor) => (
          <li key={factor}>{factor}</li>
        ))}
      </Typography>
      {differs && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          The register now suggests {suggestion.rating.toLowerCase()} risk. Review to confirm or
          record why not.
        </Alert>
      )}
      {(holder.riskReviews ?? []).length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            History
          </Typography>
          {holder.riskReviews!.map((review) => (
            <Typography key={review.id} variant="body2" sx={{ mt: 0.5 }}>
              {date(review.date)} · {review.rating.toLowerCase()} · {review.reviewedBy}
              {review.notes ? ` · ${review.notes}` : ''}
            </Typography>
          ))}
        </Box>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Review risk rating</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Rating"
              value={rating}
              onChange={(event) => setRating(event.target.value as RiskRating)}
              helperText={`Next review ${date(nextReviewDate(rating, DEMO_TODAY))}`}
            >
              {(['LOW', 'MEDIUM', 'HIGH'] as const).map((value) => (
                <MenuItem key={value} value={value}>
                  {value.charAt(0) + value.slice(1).toLowerCase()}
                  {value === suggestion.rating ? ' (suggested)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={
                rating === suggestion.rating
                  ? 'Notes (optional)'
                  : 'Why it differs from the suggestion'
              }
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={rating !== suggestion.rating && !notes.trim()}
            onClick={() => {
              dispatch({
                type: 'HOLDER_RISK_REVIEW',
                holderId: holder.id,
                rating,
                suggested: suggestion.rating,
                factors: suggestion.factors,
                notes: notes.trim(),
              });
              toast('Risk rating recorded');
              setOpen(false);
            }}
          >
            Confirm rating
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
}
